/** @file Dopesheet key addressing: row targets and channels to KeyRefs and back, ids, command selectors. */
import type { TimelineGroupInput, TimelineRow } from '@pose2d/editor-geometry/types';
import type { Id } from '@pose2d/format/types';
import type { KeyRef } from '../../../stores/types';

const DRAW_ORDER = '#drawOrder';
const EVENTS = '#events';
const EVENT_LANE = 'event:';

/** Where a key sits in the dopesheet: its group target and its channel row (`event:<eventId>` for an event lane). */
export interface RowAddress {
  target: string;
  channel: string;
}

/**
 * KeyRef encoding (consumed by the curve editor E4 and the inspector E2): bone, slot and IK keys carry the entity id
 * and the animation.setKey channel; deform keys the attachment id and `deform`; draw-order keys `#drawOrder` and
 * `drawOrder`; event keys the event id and `event`.
 */
export const dopeKeys = {
  /** Group target of the draw-order row (no entity id starts with `#`). */
  DRAW_ORDER,
  /** Group target of the event lanes. */
  EVENTS,
  /** The KeyRef of the key at `frame` on channel row (`target`, `channel`). */
  refOf(animation: Id, target: string, channel: string, frame: number): KeyRef {
    if (channel.startsWith(EVENT_LANE)) return { animation, target: channel.slice(EVENT_LANE.length), channel: 'event', frame };
    return { animation, target, channel, frame };
  },
  /** The channel row a KeyRef sits on (refOf's inverse). */
  rowOf(ref: KeyRef): RowAddress {
    return ref.channel === 'event' ? { target: EVENTS, channel: `${EVENT_LANE}${ref.target}` } : { target: ref.target, channel: ref.channel };
  },
  /** The KeyRefs behind the key at `frame` on `row`: itself on a channel row, every channel keyed there on a group row. */
  refsAt(animation: Id, groups: ReadonlyMap<string, TimelineGroupInput>, row: TimelineRow, frame: number): KeyRef[] {
    if (row.channel !== null) return [dopeKeys.refOf(animation, row.target, row.channel, frame)];
    const channels = groups.get(row.target)?.channels ?? [];
    return channels.filter((c) => c.frames.includes(frame)).map((c) => dopeKeys.refOf(animation, row.target, c.channel, frame));
  },
  /** Selection identity of a key. */
  idOf(ref: KeyRef): string {
    return `${ref.animation}|${ref.target}|${ref.channel}|${String(ref.frame)}`;
  },
  /** Drawing mark of a key on a row; `channel` null = the group row. */
  mark(target: string, channel: string | null, frame: number): string {
    return `${target}|${channel ?? '*'}|${String(frame)}`;
  },
  /** Marks of selected keys: each on its channel row and on its group row. */
  marks(refs: readonly KeyRef[]): Set<string> {
    const out = new Set<string>();
    for (const ref of refs) {
      const row = dopeKeys.rowOf(ref);
      out.add(dopeKeys.mark(row.target, row.channel, ref.frame));
      out.add(dopeKeys.mark(row.target, null, ref.frame));
    }
    return out;
  },
  /** The `channel` + `target` part of animation.moveKeys / deleteKeys input (draw order takes no target). */
  selector(ref: KeyRef): { channel: string; target?: string } {
    return ref.channel === 'drawOrder' ? { channel: 'drawOrder' } : { channel: ref.channel, target: ref.target };
  },
};
